import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, LayoutGrid, X } from 'lucide-react';
import { useGames } from '@/Context/GameContext';
import GameCards from '@/components/home/GameCards/GameCards';
import GameGrid from '@/components/common/GlobalSearchModal/components/GameGrid';
import ProviderModal from '@/components/common/ProviderModal/ProviderModal';
import SearchBar from '@/components/common/GlobalSearchModal/components/SearchBar';
import CasinoBannerSlider from '@/components/casino/CasinoBannerSlider/CasinoBannerSlider';
import { fetchPragmaticSlots, launchJsGame } from '@/services/api';
import styles from './Casino.module.css';

/**
 * Casino Page Component
 * Main casino page with provider filtering, search, and game sections
 * Uses GameContext to avoid redundant data fetching
 */
const Casino = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  
  const {
    searchResults,
    isLoading,
    currentQuery,
    selectedVendors,
    searchGames,
    vendorDisplayNames,
    setSelectedVendors,
    handleGameLaunch,
    loadMoreGames,
    hasMorePages
  } = useGames();

  const [searchInput, setSearchInput] = useState('');
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [activeTab, setActiveTab] = useState(category || 'lobby');
  const searchTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Category tabs configuration
  const categoryTabs = [
    { id: 'lobby', label: 'Lobby', icon: '/assets/images/casino/lobby.svg' },
    { id: 'live-casino', label: 'Live Casino', icon: '/assets/images/casino/live-casino.svg' },
    { id: 'popular-slots', label: 'Popular Slots', icon: <Grid size={18} /> },
    { id: 'game-shows', label: 'Game Shows', icon: '/assets/images/casino/game-shows.svg' },
    { id: 'crash-games', label: 'Crash Games', icon: '/assets/images/casino/crash-games.svg' },
  ];

  // Provider mappings for different categories (vendor keys)
  // Main providers for lobby (10-12 main providers including live + slots)
  const lobbyProviders = [
    'spribe', 'pragmaticplay', 'netent', 'playtech', 'bgaming',
    'ezugi', 'live88', 'sagaming', 'playngo', 'hacksawgaming',
    'yggdrasil', 'quickspin'
  ];

  // Live casino providers (live dealer providers)
  const liveCasinoProviders = [
    'ezugi', 'live88', 'sagaming', 'onetouch', 'rich88'
  ];

  // Popular slots providers
  const slotsProviders = [
    'pragmaticplay', 'netent', 'playtech', 'bgaming', 'hacksawgaming',
    'playngo', 'quickspin', 'yggdrasil', 'thunderkick', 'spribe'
  ];

  // Provider mappings for GameCards component (display names)
  const categoryProviders = {
    'live-casino': ['Ezugi', 'Live88', 'SA Gaming', 'OneTouch', 'Rich88'],
    'popular-slots': ['Pragmatic Play', 'NetEnt', 'Playtech', 'BGaming', 'Hacksaw Gaming', 'PlayNGo', 'Quickspin', 'Yggdrasil', 'Thunderkick', 'Spribe'],
    'game-shows': ['Ezugi', 'Pragmatic Play'],
    'crash-games': ['Spribe'],
    'lobby': [] // Lobby shows all games based on selected providers
  };

  // Get providers for current tab (for filtering games, but NOT for selectedVendors state)
  // Tab selection and provider selection are independent
  const getProvidersForTab = useCallback((tabId) => {
    if (tabId === 'lobby') {
      return lobbyProviders;
    } else if (tabId === 'live-casino') {
      return liveCasinoProviders;
    } else if (tabId === 'popular-slots') {
      return slotsProviders;
    } else if (tabId === 'game-shows') {
      return ['ezugi', 'pragmaticplay'];
    } else if (tabId === 'crash-games') {
      return ['spribe'];
    }
    return [];
  }, []);

  // Reset state when page loads/initializes
  useEffect(() => {
    // Reset provider selection on mount
    setSelectedVendors([]);
    setSearchInput('');
    hasInitializedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Initialize search when tab changes - Use category providers for filtering
  const prevTabRef = useRef(null);
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    // Skip if tab hasn't changed (not initial mount)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On initial mount, get providers for the active tab and search
      const categoryProviders = getProvidersForTab(activeTab);
      if (categoryProviders.length > 0) {
        searchGames('', categoryProviders);
      }
      return;
    } else if (prevTabRef.current === activeTab) {
      return;
    }
    prevTabRef.current = activeTab;

    // Get providers for this tab category (for filtering games)
    const categoryProviders = getProvidersForTab(activeTab);
    
    // If a provider is explicitly selected, use that; otherwise use category providers
    const providersToUse = selectedVendors.length === 1 
      ? selectedVendors  // Use explicitly selected provider
      : categoryProviders; // Use category default providers for filtering

    // Trigger search with appropriate providers
    if (providersToUse.length > 0) {
      searchGames(searchInput, providersToUse);
    }
  }, [activeTab, searchInput, searchGames, getProvidersForTab, selectedVendors]);

  // Note: Initial search is now handled in the tab change effect above

  // Handle search input with debounce
  const handleSearchInput = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchGames(value, selectedVendors);
    }, 300);
  }, [selectedVendors, searchGames]);

  // Handle provider selection change - Single provider selection
  const handleProvidersChange = useCallback((newProviders) => {
    setSelectedVendors(newProviders);
    // Trigger search with the selected provider(s)
    searchGames(searchInput, newProviders);
  }, [searchInput, setSelectedVendors, searchGames]);

  // Handle tab change - Reset provider selection and show data for selected tab
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    hasInitializedRef.current = false; // Reset initialization flag
    
    // Reset provider selection when tab changes
    setSelectedVendors([]);
    
    // Get providers for this tab category (for filtering games)
    const categoryProviders = getProvidersForTab(tabId);

    // Use category providers for filtering games for this tab
    if (categoryProviders.length > 0) {
      searchGames(searchInput, categoryProviders);
    }
    
    // Update URL if needed
    if (tabId !== 'lobby') {
      navigate(`/casino/${tabId}`, { replace: true });
    } else {
      navigate('/casino', { replace: true });
    }
  }, [navigate, searchInput, searchGames, getProvidersForTab, setSelectedVendors]);

  // Get providers for current category (for GameCards component - display names)
  const getCurrentCategoryProviders = () => {
    return categoryProviders[activeTab] || [];
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Sync active tab with URL param
  useEffect(() => {
    if (category && category !== activeTab) {
      setActiveTab(category);
    }
  }, [category, activeTab]);

  return (
    <div className={styles.container}>
      {/* Banner Slider Section */}
      <section className={styles.bannerSection}>
        <CasinoBannerSlider />
      </section>

      {/* Header Section */}
      <div className={styles.header}>
        <h1 className={styles.title}>Casino</h1>
        
        {/* Search and Filter Bar */}
        <div className={styles.searchAndFilter}>
          <div 
            className={styles.searchBarWrapper}
            onClick={() => {
              setShowProviderModal(true);
              // navigate('/modal?mode=search');
            }}
          >
            <SearchBar
              value={searchInput}
              onChange={handleSearchInput}
              placeholder="Search games..."
              // readOnly
            />
          </div>
          
          {selectedVendors.length === 1 ? (
            /* Show selected provider with close button */
            <div 
              className={styles.providerChip}
              onClick={() => setShowProviderModal(true)}
            >
              <img 
                src={`/assets/images/Providers/${selectedVendors[0].toLowerCase()}.png`}
                alt={vendorDisplayNames[selectedVendors[0]]}
                className={styles.providerChipLogo}
                onError={(e) => {
                  // Fallback to default icon if provider logo not found
                  e.target.src = '/assets/images/provider-slider.svg';
                  e.target.className = styles.providerIcon;
                }}
              />
              <span className={styles.providerChipName}>
                {vendorDisplayNames[selectedVendors[0]] || selectedVendors[0]}
              </span>
              <button
                className={styles.providerChipClose}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedVendors([]);
                  searchGames(searchInput, []);
                }}
                aria-label="Clear provider filter"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            /* Show default Providers button */
            <button
              className={`${styles.providerButton} ${selectedVendors.length > 0 ? styles.providerButtonActive : ''}`}
              onClick={() => setShowProviderModal(true)}
            >
              <img 
                src="/assets/images/provider-slider.svg" 
                alt="Provider filter" 
                className={styles.providerIcon}
              />
              <span>
                Providers
              </span>
            </button>
          )}
        </div>

        {/* Category Tabs */}
        <div className={styles.tabs}>
          {categoryTabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {typeof tab.icon === 'string' ? (
                <img src={tab.icon} alt={tab.label} className={styles.tabIcon} />
              ) : (
                tab.icon
              )}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.content}>
        {activeTab === 'lobby' ? (
          /* Lobby View - Shows all sections with filtered games */
          <div className={styles.lobbyView}>
            {/* Show provider-specific games when a single provider is selected */}
            {selectedVendors.length === 1 ? (
              /* Only show provider section when a provider is selected */
              <div className={styles.gamesSection}>
                {/* Provider Heading */}
                <div className={styles.providerHeading}>
                  <h2 className={styles.providerTitle}>
                    {vendorDisplayNames[selectedVendors[0]] || selectedVendors[0]}
                  </h2>
                  <button
                    className={styles.clearProviderButton}
                    onClick={() => {
                      setSelectedVendors([]);
                      searchGames(searchInput, []);
                    }}
                    aria-label="Clear provider filter"
                  >
                    <X size={16} />
                  </button>
                </div>
                <GameGrid
                  games={searchResults}
                  viewMode="grid"
                  onGameClick={(game) => handleGameLaunch(game, navigate)}
                  isLoading={isLoading}
                />
                {hasMorePages && searchResults.length > 0 && (
                  <div className={styles.loadMoreContainer}>
                    <button
                      className={styles.loadMoreButton}
                      onClick={loadMoreGames}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Loading...' : 'Load More Games'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Show all sections when no provider is selected */
              <>
                {/* All Games Section */}
                <div className={styles.gamesSection}>
                  <h2 className={styles.sectionTitle}>All Games</h2>
                  <GameGrid
                    games={searchResults}
                    viewMode="grid"
                    onGameClick={(game) => handleGameLaunch(game, navigate)}
                    isLoading={isLoading}
                  />
                  {hasMorePages && searchResults.length > 0 && (
                    <div className={styles.loadMoreContainer}>
                      <button
                        className={styles.loadMoreButton}
                        onClick={loadMoreGames}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Loading...' : 'Load More Games'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Live Casino Section */}
                <section className={styles.gameSection}>
                  <GameCards
                    title="Live Casino"
                    providers={categoryProviders['live-casino']}
                  />
                </section>

                {/* Popular Slots Section */}
                <section className={styles.gameSection}>
                  <GameCards
                    title="Popular Slots"
                    fetchFunction={fetchPragmaticSlots}
                    launchFunction={launchJsGame}
                    queryKey="pragmaticSlots"
                  />
                </section>

                {/* Game Shows Section */}
                <section className={styles.gameSection}>
                  <GameCards
                    title="Game Shows"
                    providers={categoryProviders['game-shows']}
                  />
                </section>

                {/* Crash Games Section */}
                <section className={styles.gameSection}>
                  <GameCards
                    title="Crash Games"
                    providers={categoryProviders['crash-games']}
                  />
                </section>
              </>
            )}
          </div>
        ) : (
          /* Category View - Shows games for specific category using GameContext search results */
          <div className={styles.categoryView}>
            {/* Show provider heading when a single provider is selected */}
            {selectedVendors.length === 1 && (
              <div className={styles.providerHeading}>
                <h2 className={styles.providerTitle}>
                  {vendorDisplayNames[selectedVendors[0]] || selectedVendors[0]}
                </h2>
                <button
                  className={styles.clearProviderButton}
                  onClick={() => {
                    setSelectedVendors([]);
                    searchGames(searchInput, []);
                  }}
                  aria-label="Clear provider filter"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            <GameGrid
              games={searchResults}
              viewMode="grid"
              onGameClick={(game) => handleGameLaunch(game, navigate)}
              isLoading={isLoading}
            />
            {hasMorePages && searchResults.length > 0 && (
              <div className={styles.loadMoreContainer}>
                <button
                  className={styles.loadMoreButton}
                  onClick={loadMoreGames}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More Games'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Provider Modal */}
      <ProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onProvidersChange={handleProvidersChange}
      />
    </div>
  );
};

export default Casino;
